/* eslint-disable max-lines */
// deps
import React, { memo, useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { safeJsonParse, useEffectOnce } from "@swiggy-private/core-ui";

// helpers
import {
    getCartItemListFromBilledLineItems,
    getStealDealPaymentSuccessData,
    isDinecashStealStoreFlow,
    openHelpWebView,
} from "@dineout-payment-success-ui/helpers/index";

// types
import {
    DineoutPaymentSuccessFlowType,
    IDineoutPaymentSuccess,
    IMetaData,
} from "@dineout-payment-success-ui/interfaces";
import {
    CheckoutContext,
    closeWebview,
    DineoutSuccessActionPayload,
    getBridgeSdk,
    getDownstreamHostsValue,
    IBrandInfo,
} from "@swiggy-private/checkout-core";

// constants
import { BILL_TEXTS, HELP_CTA_TEXT } from "@dineout-payment-success-ui/constants";

// components
import { Header } from "./components/Header";
import { Footer } from "./components/payment-success-footer";
import { BillDetails } from "./components/payment-success-bills-details";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as ArrowBack } from "@swiggy-private/dls-web-icons/dist/web/generic/arrowBack/20";
import { default as HelpIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chat/20";
import StealDealStatusBanner from "./components/BookingStatusBanner/StealDealStatusBanner";
import { CartItemListRenderer } from "@swiggy-private/dls-web-recipe-do-web/dist/components/CartItemListRenderer";

// styles
import {
    AnimatedScrollableContent,
    ContentDiv,
    DineoutWrapper,
    ScrollableContent,
    BillWrapper,
    HeaderTicketWrapper,
    BackIconWrapper,
    HeaderRightSectionWrapper,
    DividerWrapper,
    StealDealOrderDetailsWrapper,
} from "./styles";
import { HeaderWrapper, HelpIconWrapper } from "./styles/SuccessPage.styles";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SUCCESS_PAGE_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { format } from "@swiggy-private/js-utils";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import { useDinecashStoreAnalytics } from "@dineout-payment-success-ui/hooks/useDinecashStoreAnalytics";

interface IStealDealSuccessPageProps extends IDineoutPaymentSuccess {
    reloadSuccessPage: VoidFunction;
    flowType?: DineoutPaymentSuccessFlowType;
}

const STEAL_DEAL_SUCCESS_TEXT_MAP = {
    ORDER_DETAILS: "Order Details",
    COUNT_OF_VOUCHERS: "{0} vouchers",
    VIEW_ALL_VOUCHERS: "View all vouchers",
    REDEMPTION_DETAILS: "Redemption details",
};

const StealDealSuccessPage: React.FC<IStealDealSuccessPageProps> = (props) => {
    const { checkoutState } = useContext(CheckoutContext);
    const orderData = props?.data?.orders?.[0];
    const confirmOrderData = orderData?.orderJobs?.[0];
    const metaDataString = confirmOrderData?.metadata || "{}";
    const metaDataJson = safeJsonParse<IMetaData>(metaDataString);
    const [isScrolled, setIsScrolled] = useState(false);

    const isCheckoutFlow = useMemo(() => {
        return Boolean((checkoutState.payload as DineoutSuccessActionPayload)?.isCheckoutFlow);
    }, [checkoutState?.payload]);

    const {
        clickHelpBtnAnalytics,
        onHelpImpression,
        clickDoneCta,
        onDoneCtaImpression,
        onRedemptionDetailsImpression,
    } = useDinecashStoreAnalytics(isCheckoutFlow, orderData?.orderId, props?.flowType);

    const { billDetailsInfo, groupedItems, brandInfo, aggregatedItemsInfo } =
        getStealDealPaymentSuccessData(metaDataString);

    const billedLineItemList = useMemo(
        () => getCartItemListFromBilledLineItems(metaDataJson?.bill?.billedLineItems),
        [metaDataJson?.bill?.billedLineItems],
    );

    const bookingStatus = useMemo(() => confirmOrderData?.status?.toLowerCase(), [confirmOrderData?.status]);

    const isDinecashStealDealFlow = useMemo(
        () => isDinecashStealStoreFlow(metaDataJson?.bill?.saleMode, metaDataJson?.dealCategory),
        [metaDataJson],
    );

    const bookingStatusBannerSubtitle = useMemo(
        () => metaDataJson?.ctx?.bookingStatusBannerSubtitle,
        [metaDataJson?.ctx?.bookingStatusBannerSubtitle],
    );

    const bookingStatusBannerTitle = useMemo(
        () => metaDataJson?.ctx?.bookingStatusBannerTitle?.text,
        [metaDataJson?.ctx?.bookingStatusBannerTitle],
    );

    const bookingId = useMemo(() => orderData?.orderId, [orderData]);

    const onClickBack = useCallback(() => {
        closeWebview();
    }, []);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        setIsScrolled(scrollTop > 0);
    }, []);

    const redirectToVouchersPage = useCallback(() => {
        try {
            const myVouchersDeeplink = getDownstreamHostsValue("STEAL_STORE_MY_VOUCHERS_URL");
            if (!myVouchersDeeplink) {
                return;
            }

            getBridgeSdk().navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: `swiggy://webviewV2?webview_url=${encodeURIComponent(myVouchersDeeplink)}`,
                params: {
                    shouldCloseWebview: true,
                },
            });
            return;
        } catch {
            // noop
        }
    }, []);

    const handleViewHelp = useCallback(() => {
        clickHelpBtnAnalytics();
        openHelpWebView({ orderId: orderData?.orderId || "", isStealStoreFlow: true });
    }, [orderData?.orderId, clickHelpBtnAnalytics]);

    useEffectOnce(() => {
        onHelpImpression();
    });

    return (
        <DineoutWrapper onScroll={handleScroll}>
            <AnimatedScrollableContent>
                <ScrollableContent skipTopPadding isApp>
                    <ContentDiv>
                        <HeaderWrapper isScrolled={isScrolled}>
                            {!isCheckoutFlow && (
                                <BackIconWrapper>
                                    <GenericIcon
                                        Icon={ArrowBack}
                                        fillColor="text_Button_Tertiary_Low"
                                        onClick={onClickBack}
                                    />
                                </BackIconWrapper>
                            )}
                            <HeaderRightSectionWrapper>
                                {!isCheckoutFlow && (
                                    <HelpIconWrapper onClick={handleViewHelp}>
                                        <GenericIcon Icon={HelpIcon} fillColor="text_Button_Tertiary_Low" />
                                        <Text variant="V2.overline.neutral.medium" color="text_Button_Tertiary_Low">
                                            {HELP_CTA_TEXT}
                                        </Text>
                                    </HelpIconWrapper>
                                )}
                            </HeaderRightSectionWrapper>
                        </HeaderWrapper>
                        {bookingStatus && (
                            <StealDealStatusBanner
                                bookingStatus={bookingStatus}
                                bookingId={bookingId}
                                bookingStatusText={bookingStatusBannerTitle}
                                bookingStatusBannerSubtitle={bookingStatusBannerSubtitle}
                            />
                        )}
                        <StealDealOrderDetailsWrapper data-testid={SUCCESS_PAGE_TEST_IDS.TABLE_BOOKING_INFO}>
                            <HeaderTicketWrapper>
                                <Header
                                    title={STEAL_DEAL_SUCCESS_TEXT_MAP.ORDER_DETAILS}
                                    subTitle={aggregatedItemsInfo?.ticketCountTitle || ""}
                                />

                                {groupedItems &&
                                    groupedItems.map((brand, index) => {
                                        const brandName = brandInfo?.find(
                                            (brandObj: IBrandInfo) => brandObj?.id === brand?.brandId,
                                        )?.name;

                                        return (
                                            <CartItemListRenderer
                                                key={brand?.brandId ?? index}
                                                title={brandName}
                                                itemCountTitle={format(
                                                    STEAL_DEAL_SUCCESS_TEXT_MAP.COUNT_OF_VOUCHERS,
                                                    brand?.itemTotalCount,
                                                )}
                                                itemList={brand.items}
                                                showQuantity={true}
                                                enableSoldOutComs={true}
                                                showInfoIcon={false}
                                            />
                                        );
                                    })}
                            </HeaderTicketWrapper>

                            {isDinecashStealDealFlow && (
                                <HeaderTicketWrapper>
                                    <Header title={STEAL_DEAL_SUCCESS_TEXT_MAP.REDEMPTION_DETAILS} />
                                    <CartItemListRenderer
                                        itemList={billedLineItemList}
                                        onLoadImpressionEvent={onRedemptionDetailsImpression}
                                    />
                                </HeaderTicketWrapper>
                            )}
                        </StealDealOrderDetailsWrapper>

                        {!isDinecashStealDealFlow && (
                            <BillWrapper showHorizontalMargin>
                                <BillDetails billEntries={billDetailsInfo} title={BILL_TEXTS.BILL_DETAILS_TEXT} />
                            </BillWrapper>
                        )}
                        <DividerWrapper />
                    </ContentDiv>
                </ScrollableContent>
                <Footer
                    doneClickHandler={redirectToVouchersPage}
                    viewHelpClickHandler={handleViewHelp}
                    showHelpCta={isCheckoutFlow}
                    customDoneCtaText={STEAL_DEAL_SUCCESS_TEXT_MAP.VIEW_ALL_VOUCHERS}
                    isFullWidthCta
                    doneClickEventHandler={clickDoneCta}
                    doneImpressionEventHandler={onDoneCtaImpression}
                />
            </AnimatedScrollableContent>
        </DineoutWrapper>
    );
};

export default memo(StealDealSuccessPage);
